import React, { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useAppDispatch, useAppSelector } from '../../Store/store.ts';
import { ButtonHelper } from '../../Helpers/ButtonHelper.ts';
import ReactDOM from 'react-dom';

import {
  setShowFitAssistant,
  setShowFitGuideModal,
  setShowFitTipsModal,
  setStepEnum,
} from '../../Slices/FitAssistantSlice';
import { IsProductPage } from '../../../FitAssistantShared/IsProductPage.js';
import { getIconClassByFitAssistantStatus } from '../../../FitAssistantShared/Functions.js';
import { onFitAssistantOpen, GAtrackAddToCart } from '../../Analytics/Google.ts';
import {
  CarInformationSelector,
  CurrentStepSelector,
  FitStatusSelector,
  HasLoadedFromCookieSelector,
  InitialDataSelector,
  TranslationsSelector,
} from '../../Selectors/FitAssistantSelectors';
import {
  FitTipsSelector,
  MatchingProductsSelector,
  SolutionComponentsSelector,
} from '../../Selectors/MatchingProductsSelectors';
import FitAssistantStatus from '../../Enums/FitAssistantStatus.ts';
import FitAssistantStep from '../../Enums/FitAssistantStep.ts';
import FitTipsModal from '../Modal/FitTipsModal.tsx';
import FitGuideModal from '../Modal/FitGuideModal.tsx';

import './FitAssistantButton.less';
import { TrackAddToCart, trackOpen, trackOpenEmpty } from '../../Analytics/Adobe.ts';
import FitAssistantVariation from '../../Enums/FitAssistantVariation.ts';

const FitAssistantButton = () => {
  const dispatch = useAppDispatch();
  const carInformation = useAppSelector(CarInformationSelector);
  const currentStep = useAppSelector(CurrentStepSelector);
  const fitStatus = useAppSelector(FitStatusSelector);
  const initialData = useAppSelector(InitialDataSelector);
  const translations = useAppSelector(TranslationsSelector);
  const matchingProducts = useAppSelector(MatchingProductsSelector);
  const solutionComponents = useAppSelector(SolutionComponentsSelector);
  const hasLoadedFromCookie = useAppSelector(HasLoadedFromCookieSelector);
  const fitTips = useAppSelector(FitTipsSelector);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const portalTarget = document.getElementById('fitAssistantHeaderPortal');

  const handleScroll = useDebouncedCallback(() => {
    ButtonHelper.HandleStickyButtonOnScroll(buttonRef, setIsSticky);
  }, 20);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    ButtonHelper.HandleStickyButtonOnScroll(buttonRef, setIsSticky);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleButtonClick = () => {
    onFitAssistantOpen(carInformation);

    let shortVersions = [
      FitAssistantVariation.CarSeats,
      FitAssistantVariation.DogCrates,
      FitAssistantVariation.TruckBedBikeRacks,
      FitAssistantVariation.TruckAndSpareTireBikeRacks,
    ];

    let hasCarVariation = carInformation.find((x) => x.parameterName === FitAssistantStep.Variation);
    let yearFirstHasModel =
      initialData.IsYearFirst && carInformation.find((x) => x.parameterName === FitAssistantStep.Model);
    let brandFirstHasYear =
      !initialData.IsYearFirst && carInformation.find((x) => x.parameterName === FitAssistantStep.Year);

    if (
      (shortVersions.includes(initialData.FitAssistantVariation) && (yearFirstHasModel || brandFirstHasYear)) ||
      hasCarVariation
    ) {
      trackOpen(initialData.FitAssistantVariation, initialData.IsYearFirst, currentStep, carInformation);
    } else if (fitStatus !== FitAssistantStatus.RackMounting) {
      trackOpenEmpty(initialData.FitAssistantVariation, initialData.IsYearFirst, carInformation, currentStep);
    }

    document.dispatchEvent(new Event('faOpened'));
    dispatch(setShowFitAssistant(true));
  };

  const renderSolutionComponents = () => {
    return solutionComponents?.map((item, index) => {
      if (item.Name.includes('Kit')) {
        return <li key={index}>{item.Name}</li>;
      }
      return (
        <li key={index}>
          {item.Name} {item.Webtitle2} | {item.ProductId}
        </li>
      );
    });
  };

  const FitInformation = () => {
    return (
      <div className="header-wrapper mb-5">
        <h5 className={`heading5 mb-0`}>
          {translations?.fit}
          <span className="fit-status paragraph">{ButtonHelper.GetStatusLabel(fitStatus, translations)}</span>
        </h5>
      </div>
    );
  };

  const FitAssistantContent = () => {
    return (
      <div className="fit-assistant">
        {IsProductPage && <FitInformation />}

        <button
          className={`fit-assistant-button ${IsProductPage ? 'pdp' : ''}`}
          onClick={handleButtonClick}
          tabIndex={0}
        >
          <div className={`d-flex  ${IsProductPage ? 'align-items-center' : ''}`}>
            <i
              className={`fit-indicator me-5 ${fitStatus} ${IsProductPage ? getIconClassByFitAssistantStatus(fitStatus) : 'le-icon-car'}`}
            ></i>
            {<span className={`heading6 ${fitStatus} text-start d-flex align-items-center`}>
              {ButtonHelper.GetSelectionText(fitStatus, carInformation, translations, IsProductPage)}
            </span>}
          </div>
          {!IsProductPage && (
            <div className="icon-wrapper--arrow">
              <i className="le-icon-arrow rot-180"></i>
            </div>
          )}
        </button>
        {IsProductPage && currentStep === FitAssistantStep.OtherFits && (
          <div className="my-3">
            <span className="text-color--gray80">{translations.otherFitsText}</span>
            <a href={initialData.ParentPageLink} className="text-decoration-underline text-color--gray80">
              {translations.exploreAlternatives}
            </a>
          </div>
        )}
        {IsProductPage && currentStep === FitAssistantStep.NoFits && (
          <div className="my-3">
            <span className="text-color--gray80">{translations.noFitsText}</span>
          </div>
        )}
        {IsProductPage && currentStep === FitAssistantStep.Unconfirmed && (
          <div className="my-3">
            <span className="text-color--gray80">{translations.unconfirmedText}</span>
          </div>
        )}
        {fitTips.length > 0 && IsProductPage && (
          <div onClick={() => dispatch(setShowFitTipsModal(true))} className="fit-tip-box p-5 mt-5 d-flex">
            <i className="le-icon-info me-3"></i>
            <div className="d-flex flex-column">
              <span className="mb-1">{translations.vehicleFitTipsTitle}</span>
              <span className="action-text">{translations.learnMore}</span>
            </div>
          </div>
        )}
        {solutionComponents != null && solutionComponents.length > 0 && (
          <div className="my-5 text-color--gray80">
            <span>{translations.includedToFit}</span>
            <ul>{renderSolutionComponents()}</ul>
          </div>
        )}
        {IsProductPage &&
          initialData.FitAssistantVariation === FitAssistantVariation.CarRoofRacks &&
          matchingProducts === null &&
          hasLoadedFromCookie && (
            <div className="rrs-button-section">
              <hr className="my-7" />
              <button
                className="rrs-trigger-button btn btn-block thule-btn-shop overflow-hidden"
                onClick={() => {
                  GAtrackAddToCart();
                  TrackAddToCart();
                  dispatch(setStepEnum(FitAssistantStep.VehicleInfoNeeded));
                  dispatch(setShowFitAssistant(true));
                }}
              >
                {translations.addToCart}
              </button>
            </div>
          )}
      </div>
    );
  };

  return (
    <>
      <div ref={buttonRef} />
      {portalTarget && isSticky && !IsProductPage ? (
        <>{ReactDOM.createPortal(<FitAssistantContent />, portalTarget)}
        <div className='fit-assistant-spacer'></div>
        </>
      ) : (
        <FitAssistantContent />
      )}
      {initialData.FitGuide?.LinkText && IsProductPage && <FitGuideModal />}
      {fitTips.length > 0 && IsProductPage && <FitTipsModal />}
    </>
  );
};

export default FitAssistantButton;
